import { ProductObject } from "app/api/product/product.type";
import { storageKey } from "./storageKeys";

function toSnakeCase(str: string): string {
  if (str) {
    return str
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .replace(/\s+|[-]+/g, "_")
      .toLowerCase();
  }
  return "";
}

function removeSnakeCase(str: string): string {
  if (str) {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  return "";
}

function removeKebabCase(str: string): string {
  if (str) {
    const nonKababCase = str
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return removeSnakeCase(nonKababCase);
  }
  return "";
}

function toTitleCase(str: string): string {
  if (!str) return "";

  // Replace snake_case and kebab-case with spaces
  let cleaned = str.replace(/[_-]+/g, " ");

  // Add a space before any camelCase or PascalCase capital letter (except first char)
  cleaned = cleaned.replace(/([a-z])([A-Z])/g, "$1 $2");

  // Normalize multiple spaces to one
  cleaned = cleaned.replace(/\s+/g, " ").trim();

  // Convert to title case
  return cleaned
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

interface PriceBreak {
  qty: number;
  price: number;
}

function getRandomData(arr: Array<ProductObject>): ProductObject {
  if (arr.length > 0) {
    const randomIndex = getRandomInt(0, arr.length - 1);
    return arr[randomIndex];
  }

  return {} as ProductObject;
}

function getRandomInt(min: number, max: number): number {
  const range = max - min + 1;
  const randomBytes = new Uint32Array(1);
  crypto.getRandomValues(randomBytes);

  return min + (randomBytes[0] % range);
}

function calculatePercentageDiscount(percentage: number, total: number) {
  return (percentage / 100) * total;
}

function camelCaseToTitleCase(str: string) {
  if (str) {
    return str
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (match) => match.toUpperCase())
      .trim();
  }
  return "";
}

function formatToTwoDecimalPlaces(num: number): string {
  if (num && typeof num === "number" && !isNaN(num)) {
    return addSpacesToNumber(num);
  }
  return "0.00";
}

function formatToTwoDecimalPlacesNumber(num: number): number {
  if (typeof num === "number" && !isNaN(num)) {
    return Math.round(num * 100) / 100;
  }
  return 0;
}

function addSpacesToNumber(num: number): string {
  if (num) {
    return num
      .toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      .replace(/,/g, " ");
  }
  return "0.00";
}

function addSpacesToInteger(num: number): string {
  if (num) {
    return num
      .toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/,/g, " ");
  }
  return "0";
}
/**
 * Rounds a number to a specified number of decimal places.
 *
 * @param {number} num - The number to be rounded.
 * @param {number} decimals - The number of decimal places to round to.
 * @returns {number} - The rounded number.
 */
function roundToDecimal(num: number, decimals: number) {
  if (num) {
    const factor = Math.pow(10, decimals);
    return Math.round(num * factor) / factor;
  }
  return 0;
}

function setPageLimit(limit: number) {
  localStorage.setItem(storageKey.pageLimit, String(limit));
  const cookieValue = `pageLimit=${limit}`;
  const options = [
    "path=/", // Accessible across the entire site
    `max-age=${60 * 60 * 24 * 7}`, // Expires in 7 days
    process.env.NODE_ENV === "production" ? "secure" : "", // Secure flag in production
    "sameSite=lax", // Prevent CSRF attacks
  ].join("; ");

  document.cookie = `${cookieValue}; ${options}`;
}

function calculatePercentage(value: number, percentage: number) {
  return (value * percentage) / 100;
}

function isNonChromium() {
  return !("chrome" in window || "webkit" in window);
}

function toKebabCase(str: string): string {
  if (!str) return "";

  return str
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2") // Insert hyphen between camelCase or PascalCase
    .replace(/[\s_]+/g, "-") // Replace spaces or underscores with hyphen
    .replace(/-+/g, "-") // Replace multiple hyphens with a single hyphen
    .toLowerCase()
    .trim(); // Remove leading/trailing whitespace
}

function isAdditionType(priceGroups: any, type: string): boolean {
  for (const priceGroup of priceGroups) {
    for (const addition of priceGroup.additions) {
      if (addition.type?.trim() === type?.trim()) return true;
    }
  }
  return false;
}

function hasValidBrandingPositions(
  decorationOption: any,
  brandingType: string,
  isPositionInPriceGroups: (positionValue: string, brandingType: string) => boolean,
): boolean {
  // Check if decoration option has positions array (new format)
  if (decorationOption.positions && Array.isArray(decorationOption.positions)) {
    return decorationOption.positions.some(
      (position: any) => position.value && isPositionInPriceGroups(position.value, brandingType),
    );
  }

  // Check legacy format (singleColour/multiColour)
  if (decorationOption.singleColour?.positions?.length > 0) {
    return decorationOption.singleColour.positions.some(
      (position: any) => position.name && isPositionInPriceGroups(position.name, brandingType),
    );
  }

  if (decorationOption.multiColour?.positions?.length > 0) {
    return decorationOption.multiColour.positions.some(
      (position: any) => position.name && isPositionInPriceGroups(position.name, brandingType),
    );
  }

  return false;
}

function getPercentageDifference(value1: number, value2: number) {
  if (typeof value1 !== "number" || typeof value2 !== "number") {
    throw new Error("Both values must be numbers.");
  }

  // Handle cases where the sum of values is zero to avoid division by zero
  if (value1 + value2 === 0) {
    return 0; // Or handle as an error condition based on requirements
  }

  const absoluteDifference = Math.abs(value1 - value2);
  const averageOfValues = (value1 + value2) / 2;

  const percentageDifference = (absoluteDifference / averageOfValues) * 100;

  return percentageDifference;
}

export {
  setPageLimit,
  toSnakeCase,
  removeSnakeCase,
  toTitleCase,
  getRandomData,
  camelCaseToTitleCase,
  formatToTwoDecimalPlaces,
  formatToTwoDecimalPlacesNumber,
  addSpacesToInteger,
  calculatePercentage,
  calculatePercentageDiscount,
  roundToDecimal,
  isNonChromium,
  toKebabCase,
  removeKebabCase,
  isAdditionType,
  hasValidBrandingPositions,
  getPercentageDifference,
};
